import { Component, computed, effect, HostBinding, input, output, signal } from '@angular/core';

import { EaStatus } from './ea-status-bar.models';
import { DEFAULT_STATUS_BAR_DELAY_MS, eaStatusIconsByStatusType } from './ea-status-bar.utils';

/**
 * Used to indicate the status of some process in the application.
 * E.g. error, warning, success
 */
@Component({
    selector: 'ea-status-bar',
    imports: [],
    host: {
        role: 'status'
    },
    templateUrl: './ea-status-bar.html',
    styleUrl: './ea-status-bar.scss',
})
export class EaStatusBar {
    @HostBinding('class')
    get hostClass(): string {
        return this.$statusType();
    };

    /** The string text that will appear in the status bar */
    public readonly $status = input.required<string>();
    /** The type of status this bar represents */
    public readonly $statusType = input.required<EaStatus>();
    /**
     * Auto-dismiss delay in milliseconds. When greater than zero, a progress bar is shown
     * and `delayElapsed` is emitted once the delay completes.
     */
    public readonly $delay = input<number>(DEFAULT_STATUS_BAR_DELAY_MS);

    /** Emits when the configured delay has elapsed */
    public readonly delayElapsed = output<void>();

    /** The icon that represents this status */
    protected readonly $statusIcon = computed(() => {
        return eaStatusIconsByStatusType[this.$statusType()];
    });

    protected readonly $showProgress = computed(() => this.$delay() > 0);
    protected readonly $progressAnimationKey = signal(0);

    constructor() {
        // Re-run whenever the delay or status text changes.
        effect((onCleanup) => {
            const delay = this.$delay();
            this.$status();

            if (delay <= 0) {
                return;
            }

            // Bump the key so the template recreates the progress fill element,
            // restarting its CSS width animation from 0%.
            this.$progressAnimationKey.update((key) => key + 1);

            const timeoutId = setTimeout(() => {
                this.delayElapsed.emit();
            }, delay);

            // Angular calls this before the effect re-runs or when the component is
            // destroyed, so a pending timeout is cleared instead of firing late.
            onCleanup(() => {
                clearTimeout(timeoutId);
            });
        });
    }
}
